import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Upload, Loader2, ClipboardList, CheckCircle2, X, Send, AlertTriangle,
  ChevronLeft, Edit2, Save, FileText, FlaskConical, BarChart3, Users, Mail,
  Eye, Unlock,
} from 'lucide-react';
import CheckinEngagement from './CheckinEngagement';

const API = '/api';

const BUCKET_META = {
  UP: { label: 'Up', color: '#48B64C' },
  FLAT: { label: 'Flat', color: '#888888' },
  DOWN: { label: 'Down', color: '#e0a020' },
  ZERO_QUIET: { label: 'Quiet / $0', color: '#e07070' },
  NEW_WITH_PRIOR: { label: 'New (with prior)', color: '#5b9bd5' },
  BRAND_NEW: { label: 'Brand new', color: '#76BB40' },
};
const BUCKET_ORDER = ['ZERO_QUIET', 'DOWN', 'FLAT', 'UP', 'NEW_WITH_PRIOR', 'BRAND_NEW'];

const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function FitterCheckin() {
  // The end-of-send report email links straight to its run: ?module=fitter-checkin&run=<id>.
  const linkedRun = (() => {
    try { return new URLSearchParams(window.location.search).get('run'); } catch { return null; }
  })();
  const [view, setView] = useState(linkedRun ? 'review' : 'upload'); // upload | runs | review
  const [runId, setRunId] = useState(linkedRun);
  const [runs, setRuns] = useState([]);

  const loadRuns = useCallback(async () => {
    try { const r = await axios.get(`${API}/fitter-checkin/runs`); setRuns(r.data.runs || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  return (
    <div className="space-y-5" data-testid="fitter-checkin-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">FITTER CHECK-IN</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">Upload QuickBooks monthly + YTD → bucketed, drafted check-ins for review and send.</p>
        </div>
        <div className="flex gap-1 border border-hg-border rounded-sm overflow-hidden text-xs font-sans">
          <button data-testid="fc-tab-upload" onClick={() => setView('upload')} className={`px-3 py-1.5 ${view === 'upload' ? 'bg-hg-green text-hg-text' : 'text-hg-text-dim'}`}>New run</button>
          <button data-testid="fc-tab-runs" onClick={() => { setView('runs'); loadRuns(); }} className={`px-3 py-1.5 ${view === 'runs' ? 'bg-hg-green text-hg-text' : 'text-hg-text-dim'}`}>Past runs</button>
        </div>
      </div>

      {view === 'upload' && <UploadPanel onDone={(id) => { setRunId(id); setView('review'); loadRuns(); }} />}
      {view === 'runs' && <RunsList runs={runs} onOpen={(id) => { setRunId(id); setView('review'); }} />}
      {view === 'review' && runId && <ReviewPanel runId={runId} onBack={() => { setView('runs'); loadRuns(); }} />}

      {(view === 'upload' || view === 'runs') && <TestingPanel />}
    </div>
  );
}

const TEST_RECIPIENTS = ['randall@henry-griffitts.com', 'grant@henry-griffitts.com'];
const TEST_TO_LABEL = 'you + Grant (randall@, grant@)';

function TestingPanel() {
  const [bucket, setBucket] = useState('DOWN');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const fire = async (key, url) => {
    setBusy(key); setMsg('');
    try {
      const r = await axios.post(`${API}${url}`);
      const to = Array.isArray(r.data?.to) ? r.data.to.join(', ') : (r.data?.to || TEST_RECIPIENTS.join(', '));
      setMsg(r.data?.campaign_id ? `Sent to ${to} (campaign ${r.data.campaign_id}).` : 'Sent.');
    } catch (e) { setMsg(e.response?.data?.detail || 'Send failed.'); }
    setBusy('');
  };

  return (
    <div className="bg-hg-surface border border-dashed border-hg-border rounded-sm p-4 space-y-3 max-w-2xl" data-testid="fc-testing">
      <div className="flex items-center gap-2">
        <FlaskConical size={14} className="text-hg-text-dim" />
        <span className="text-xs font-sans text-hg-text uppercase tracking-wider">Testing</span>
        <span className="text-[10px] font-sans text-hg-text-dim">— sends to {TEST_TO_LABEL} via the HG Hub Test group. Never touches the real fitter list.</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="fc-test-bucket" value={bucket} onChange={(e) => setBucket(e.target.value)}
          className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green">
          {BUCKET_ORDER.map((b) => <option key={b} value={b}>{BUCKET_META[b]?.label || b}</option>)}
        </select>
        <TestBtn testid="fc-test-checkin" icon={Mail} busy={busy === 'checkin'} onClick={() => fire('checkin', `/fitter-checkin/test/sample-checkin?bucket=${bucket}`)}>Send sample check-in</TestBtn>
        <TestBtn testid="fc-test-digest" icon={BarChart3} busy={busy === 'digest'} onClick={() => fire('digest', '/fitter-checkin/test/sample-digest')}>Send sample digest</TestBtn>
        <TestBtn testid="fc-test-rollup" icon={Users} busy={busy === 'rollup'} onClick={() => fire('rollup', '/fitter-checkin/test/sample-manager-rollup')}>Send sample roll-up</TestBtn>
      </div>
      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-hg-border/50">
        <span className="text-[10px] font-sans text-hg-text-dim">Live 1:1 path — to the <b className="text-hg-text">is_test</b> fitter only, via the dedicated Fitter Check-ins group:</span>
        <TestBtn testid="fc-test-fitter" icon={Send} busy={busy === 'testfitter'} onClick={() => fire('testfitter', `/fitter-checkin/test/send-test-fitter?bucket=${bucket}`)}>Send live check-in to TEST fitter</TestBtn>
      </div>
      {msg && <div className="text-[11px] font-sans text-hg-green bg-hg-green/10 border border-hg-green/30 rounded-sm px-3 py-2" data-testid="fc-test-msg">{msg}</div>}
    </div>
  );
}

function TestBtn({ testid, icon: Icon, busy, onClick, children }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {children}
    </button>
  );
}

function UploadPanel({ onDone }) {
  const [month, setMonth] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [monthlyFile, setMonthlyFile] = useState(null);
  const [ytdFile, setYtdFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const submit = async () => {
    const missing = [!month.trim() && 'Month label', !monthlyFile && 'the Monthly file', !ytdFile && 'the YTD file'].filter(Boolean);
    if (missing.length) { setError(`Required before generating: ${missing.join(', ')}.`); return; }
    setBusy(true); setError(''); setResult(null);
    const fd = new FormData();
    fd.append('month', month.trim());
    fd.append('dry_run', dryRun ? 'true' : 'false');
    fd.append('monthly_file', monthlyFile);
    fd.append('ytd_file', ytdFile);
    try {
      const r = await axios.post(`${API}/fitter-checkin/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Upload failed.'); }
    setBusy(false);
  };

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-4 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Month label *</label>
          <input data-testid="fc-month" value={month} onChange={(e) => setMonth(e.target.value)} placeholder="2026-06"
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green" />
        </div>
        <label className="flex items-center gap-2 mt-5 cursor-pointer">
          <input type="checkbox" data-testid="fc-dryrun" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="accent-hg-green" />
          <span className="text-xs font-sans text-hg-text">Dry-run (draft only — no sends)</span>
        </label>
      </div>
      <FileField label="QuickBooks — Monthly (current + prior year + prior month) (CSV)" testid="fc-monthly" file={monthlyFile} onPick={setMonthlyFile} />
      <FileField label="QuickBooks — YTD (current + prior year) (CSV)" testid="fc-ytd" file={ytdFile} onPick={setYtdFile} />
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      <button data-testid="fc-upload-btn" onClick={submit} disabled={busy}
        className="inline-flex items-center gap-2 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Process files
      </button>
      {result && (
        <div className="bg-hg-bg/60 border border-hg-green/30 rounded-sm p-3 text-xs font-sans text-hg-text space-y-1" data-testid="fc-upload-result">
          <div className="flex items-center gap-1.5 text-hg-green"><CheckCircle2 size={13} /> Drafted {result.total_drafts} check-ins · {result.unmatched_count} unmatched rows</div>
          <div className="text-hg-text-dim">Buckets: {Object.entries(result.bucket_counts || {}).map(([b, n]) => `${BUCKET_META[b]?.label || b} ${n}`).join(' · ')}</div>
          <button data-testid="fc-open-review" onClick={() => onDone(result.run_id)} className="mt-1 text-hg-green hover:underline">Review drafts →</button>
        </div>
      )}
    </div>
  );
}

function FileField({ label, testid, file, onPick }) {
  return (
    <div>
      <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">{label}</label>
      <label data-testid={testid} className="flex items-center gap-2 px-3 py-2 text-xs font-sans border border-dashed border-hg-border rounded-sm cursor-pointer hover:border-hg-green text-hg-text-dim">
        <FileText size={13} /> {file ? file.name : 'Choose CSV…'}
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} />
      </label>
    </div>
  );
}

function RunsList({ runs, onOpen }) {
  if (!runs.length) return <div className="text-center py-12 text-xs text-hg-text-dim font-sans" data-testid="fc-runs-empty">No runs yet.</div>;
  return (
    <div className="space-y-2" data-testid="fc-runs-list">
      {runs.map((r) => (
        <button key={r.id} data-testid={`fc-run-${r.id}`} onClick={() => onOpen(r.id)}
          className="w-full flex items-center justify-between bg-hg-surface border border-hg-border rounded-sm px-4 py-3 hover:border-hg-green text-left">
          <div className="flex items-center gap-3">
            <ClipboardList size={15} className="text-hg-green" />
            <div>
              <div className="text-sm font-sans text-hg-text">{r.month} {r.dry_run && <span className="text-[10px] text-yellow-400">· dry-run</span>}</div>
              <div className="text-[10px] text-hg-text-dim font-sans">{r.total_drafts} drafts · {Object.entries(r.bucket_counts || {}).map(([b, n]) => `${b} ${n}`).join(', ')}</div>
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${r.status === 'sent' ? 'bg-hg-green/10 text-hg-green' : 'bg-hg-border text-hg-text-dim'}`}>{r.status}</span>
        </button>
      ))}
    </div>
  );
}

function ReviewPanel({ runId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  // {kind: 'ok' | 'warn' | 'error', text}. A bare string rendered green said "Send
  // failed." in the success colour on 2026-10-01 — the kind is what picks the style.
  const [notif, setNotifState] = useState(null);
  const setNotif = (text, kind = 'ok') => setNotifState(text ? { kind, text } : null);
  // The send job this run is claimed by, or the last one it had. This is the SERVER's
  // record: the send runs as a background job there, so the page can be closed and
  // reopened mid-run and show the same progress.
  const [job, setJob] = useState(null);
  // Send was pressed, the Hub did not answer, and the run could not be re-read either:
  // nothing on this page is known to be current, so the Send buttons stay locked.
  const [sendUnknown, setSendUnknown] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [preview, setPreview] = useState(null);       // {html, subject, fitter_name} | null
  const [previewLoading, setPreviewLoading] = useState(false);

  // quiet = refresh behind the page without swapping it for a spinner (used while a send
  // is running, so the rows update in place).
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    let fresh = null;
    try {
      const r = await axios.get(`${API}/fitter-checkin/runs/${runId}`);
      setData(r.data); setJob(r.data.send_job || null); setSendUnknown(false);
      fresh = r.data;
    } catch (e) { if (!quiet) setError(e.response?.data?.detail || 'Failed to load run.'); }
    if (!quiet) setLoading(false);
    return fresh;   // null = could not read the run, which is not the same as an empty one
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  // Follow a running send job. The job endpoint only counts drafts, so it is cheap to
  // ask every few seconds; the run itself is re-read every half minute and when it ends.
  const jobId = job?.id;
  const jobRunning = job?.status === 'running';
  useEffect(() => {
    if (!jobId || !jobRunning) return undefined;
    let ticks = 0;
    let stopped = false;
    const timer = setInterval(async () => {
      ticks += 1;
      try {
        const r = await axios.get(`${API}/fitter-checkin/send-jobs/${jobId}`);
        if (stopped) return;
        setJob(r.data);
        if (r.data.status !== 'running' || ticks % 6 === 0) load(true);
      } catch { /* a missed poll is not news — the next one will say */ }
    }, 5000);
    return () => { stopped = true; clearInterval(timer); };
  }, [jobId, jobRunning, load]);

  const patch = async (id, body) => { await axios.patch(`${API}/fitter-checkin/drafts/${id}`, body); await load(); };
  const bulkApprove = async (bucket) => { await axios.post(`${API}/fitter-checkin/runs/${runId}/bulk-approve`, { bucket }); await load(); };
  // Send STARTS a background job and returns at once. Nothing here waits for the send:
  // on 2026-10-01 the page waited, the browser gave up at 300 s, and the banner said
  // "Send failed." over a run that sent for twenty more minutes.
  const doSend = async (approveAll) => {
    setSending(true); setNotif('');
    try {
      const r = await axios.post(`${API}/fitter-checkin/runs/${runId}/send${approveAll ? '?approve_all=true' : ''}`);
      setJob(r.data.job);
      // attached = a send was already running on this run; no second one was started.
      setNotif(r.data.message, r.data.attached ? 'warn' : 'ok');
      load(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Re-read either way: the run now says whether a send is running.
      const fresh = await load(true);
      if (detail) {
        // The Hub refused and SAID WHY — a real answer.
        setNotif(`Send not started: ${detail}`, 'error');
      } else {
        // No reason came back. That is "I do not know", never "it failed".
        setSendUnknown(!fresh);
        const how = e.response ? `the Hub answered HTTP ${e.response.status} with no reason`
          : 'the Hub did not answer';
        setNotif(`No answer when starting the send — ${how}. Whether it started is NOT known`
          + (fresh ? '; the run has been re-read, and a running send shows below.'
            : ', and the run could not be re-read. Reload this page before pressing Send again.'),
        'error');
      }
    }
    setSending(false);
  };

  // A fitter whose send started and never recorded an outcome is held until a person has
  // looked at MailerLite. This is that person saying "it did not go — let it be sent".
  const releaseClaim = async (u) => {
    if (!window.confirm(`Release ${u.name} for sending?\n\nOnly do this after checking MailerLite and finding NO check-in campaign for ${u.email} this month. If one went, releasing sends a second.`)) return;
    try {
      await axios.post(`${API}/fitter-checkin/drafts/${u.draft_id}/release-claim`);
      await load(true);
      setNotif(`${u.name} released — approved and sendable again. Nothing was sent.`, 'warn');
    } catch (e) { setNotif(e.response?.data?.detail || 'Could not release — nothing changed.', 'error'); }
  };

  // Re-ask MailerLite about every dispatched-but-unconfirmed send. Sends nothing.
  const recheck = async () => {
    setSending(true); setNotif('');
    try {
      const r = await axios.post(`${API}/fitter-checkin/resolve-pending?run_id=${runId}`);
      setNotif(`Re-checked ${r.data.checked} · confirmed ${r.data.resolved} · `
        + `did not send ${r.data.failed} (requeued) · still pending ${r.data.still_pending}`);
      await load();
    } catch (e) { setNotif(e.response?.data?.detail || 'Re-check failed — MailerLite was not read, so nothing is known to have changed.', 'error'); }
    setSending(false);
  };
  const sendApproved = () => doSend(false);
  const sendAll = () => { if (window.confirm('Approve every pending draft and send the whole batch now? (Excluded stay excluded.)')) doSend(true); };

  const enableSends = async () => {
    if (!window.confirm('Enable real sends for this run?\n\nThis only lights up the Send buttons — nothing sends until you click Send + approve. Your approvals, exclusions, and edits are all preserved.')) return;
    setEnabling(true); setNotif('');
    try { await axios.post(`${API}/fitter-checkin/runs/${runId}/enable-sends`); await load(); setNotif('Real sends enabled — nothing sent yet. Use Send approved / Send all when ready.'); }
    catch (e) { setNotif(e.response?.data?.detail || 'Could not enable sends.', 'error'); }
    setEnabling(false);
  };

  const openPreview = async (draftId) => {
    setPreviewLoading(true); setPreview({ html: '', subject: '', fitter_name: '' });
    try { const r = await axios.get(`${API}/fitter-checkin/drafts/${draftId}/preview`); setPreview(r.data); }
    catch (e) { setNotif(e.response?.data?.detail || 'Preview failed.', 'error'); setPreview(null); }
    setPreviewLoading(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  if (error) return <div className="text-xs text-red-400 font-sans">{error}</div>;
  const run = data.run;
  const grouped = data.drafts_by_bucket || {};
  const allDrafts = Object.values(grouped).flat();
  const approvedCount = allDrafts.filter((d) => d.status === 'approved').length;
  const pendingCount = data.pending_confirm ?? allDrafts.filter((d) => d.status === 'pending_confirm').length;
  // An approved draft carrying a failure reason is someone a previous run did NOT reach.
  const unreached = allDrafts.filter((d) => d.status === 'approved' && d.last_error);
  // Per-recipient detail from the last job, once it has ended.
  const sendResults = job && !jobRunning ? (job.results || []) : null;

  return (
    <div className="space-y-4" data-testid="fc-review">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text font-sans"><ChevronLeft size={13} /> Back</button>
        <div className="flex items-center gap-2">
          {run.dry_run ? (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] text-yellow-400 font-sans bg-yellow-400/10 border border-yellow-400/30 rounded-sm px-2 py-1"><AlertTriangle size={10} /> dry-run — sends disabled</span>
              <button data-testid="fc-enable-sends" onClick={enableSends} disabled={enabling || run.status === 'sent'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-yellow-400/50 text-yellow-300 rounded-sm hover:bg-yellow-400/10 disabled:opacity-40"
                title="Turn off dry-run for THIS run (keeps all your curation). Nothing sends until you click Send + approve.">
                {enabling ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />} Enable real sends
              </button>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] text-hg-green font-sans bg-hg-green/10 border border-hg-green/30 rounded-sm px-2 py-1"><CheckCircle2 size={10} /> real sends enabled</span>
          )}
          <button data-testid="fc-send-approved" onClick={sendApproved} disabled={sending || sendUnknown || jobRunning || run.dry_run || approvedCount === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send approved ({approvedCount})
          </button>
          <button data-testid="fc-send-all" onClick={sendAll} disabled={sending || sendUnknown || jobRunning || run.dry_run}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
            title="Approve every pending draft and send the whole batch in one action">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send all
          </button>
        </div>
      </div>
      {notif && (
        <div data-testid="fc-send-notif" data-kind={notif.kind}
          className={`flex items-start gap-2 text-xs font-sans rounded-sm px-3 py-2 border ${
            notif.kind === 'error' ? 'text-red-300 bg-red-400/10 border-red-400/30'
              : notif.kind === 'warn' ? 'text-amber-300 bg-amber-400/10 border-amber-400/30'
                : 'text-hg-green bg-hg-green/10 border-hg-green/30'}`}>
          {notif.kind === 'ok' ? <CheckCircle2 size={12} className="mt-0.5 shrink-0" /> : <AlertTriangle size={12} className="mt-0.5 shrink-0" />}
          <span>{notif.text}</span>
        </div>
      )}
      {job && <SendJobPanel job={job} onRelease={releaseClaim} />}

      {/* Dispatched but NOT yet confirmed delivered. Shown as its own state because
          MailerLite's 'ready' means queued, not sent — treating the two as the same
          thing is what silently lost two fitters on the July run. */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-3 text-xs font-sans text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-sm px-3 py-2" data-testid="fc-pending-confirm">
          <AlertTriangle size={12} />
          <span>{pendingCount} check-in{pendingCount === 1 ? '' : 's'} handed to MailerLite but not yet confirmed delivered — these are <strong>not</strong> counted as sent.</span>
          <button onClick={recheck} disabled={sending} data-testid="fc-recheck"
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-amber-400/50 rounded-sm hover:bg-amber-400/10 disabled:opacity-40">
            {sending ? <Loader2 size={11} className="animate-spin" /> : null} Re-check delivery
          </button>
        </div>
      )}

      {/* Fitters a previous send did not reach. Permanent failures (unsubscribed /
          bounced / junk in MailerLite) can never be fixed by pressing send again. */}
      {unreached.length > 0 && (
        <div className="border border-red-400/30 bg-red-400/5 rounded-sm px-3 py-2 space-y-1" data-testid="fc-unreached">
          <div className="text-xs font-sans text-red-300 flex items-center gap-2">
            <AlertTriangle size={12} /> {unreached.length} fitter{unreached.length === 1 ? '' : 's'} not reached by an earlier send
          </div>
          {unreached.map((d) => (
            <div key={d.id} className="text-[11px] font-sans pl-5">
              <span className="text-hg-text">{d.fitter_name}</span>{' '}
              <span className="text-hg-text-dim">{d.email}</span>{' '}
              {d.last_error_retryable
                ? <span className="text-amber-300">— retryable, press Send approved again</span>
                : <span className="text-red-300">— permanent{d.undeliverable_status ? ` (${d.undeliverable_status} in MailerLite)` : ''}, sending again will not help</span>}
              <div className="text-hg-text-dim/80 mt-0.5">{d.last_error}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-recipient outcome. A bare "errors 42" is not a diagnosis — this
          says who failed, why, and whether pressing send again will fix it. */}
      {sendResults && sendResults.some((r) => r.outcome !== 'sent') && (
        <div className="border border-hg-border rounded-sm" data-testid="fc-send-results">
          <button onClick={() => setShowResults(!showResults)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-sans text-hg-text-dim hover:text-hg-text">
            <span>
              {sendResults.filter((r) => r.outcome === 'error').length} error(s) ·{' '}
              {sendResults.filter((r) => r.outcome === 'skipped').length} skipped ·{' '}
              <span className="text-amber-300">
                {sendResults.filter((r) => r.outcome === 'error' && r.retryable).length} retryable
              </span>
            </span>
            <span>{showResults ? 'hide' : 'show detail'}</span>
          </button>
          {showResults && (
            <div className="border-t border-hg-border max-h-72 overflow-y-auto">
              {sendResults.filter((r) => r.outcome !== 'sent').map((r, i) => (
                <div key={i} className="px-3 py-1.5 border-b border-hg-border/50 last:border-0 text-[11px] font-sans">
                  <div className="flex items-center gap-2">
                    <span className={`uppercase tracking-wider text-[9px] px-1.5 py-0.5 rounded-sm ${
                      r.outcome === 'error' ? 'bg-red-400/10 text-red-400' : 'bg-hg-border text-hg-text-dim'}`}>
                      {r.outcome}
                    </span>
                    <span className="text-hg-text">{r.fitter || '—'}</span>
                    <span className="text-hg-text-dim">{r.email}</span>
                    {r.retryable && <span className="ml-auto text-amber-300">retryable</span>}
                  </div>
                  {r.reason && <div className="text-hg-text-dim mt-0.5 pl-1">{r.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <UnmatchedBanner run={run} runId={runId} onChanged={load} sales={data.pending_rule_sales} />
      <DigestSection runId={runId} dryRun={run.dry_run} />

      {/* 'partial' counts too — a run where most went out still has engagement worth reading. */}
      {(run.status === 'sent' || run.status === 'partial') && <CheckinEngagement runId={runId} monthLabel={run.month} />}

      {BUCKET_ORDER.filter((b) => grouped[b]?.length).map((b) => (
        <BucketBlock key={b} bucket={b} drafts={grouped[b]} onPatch={patch} onBulkApprove={() => bulkApprove(b)} onPreview={openPreview} disabled={run.status === 'sent' || jobRunning} />
      ))}

      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white border border-hg-border rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="fc-preview-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-hg-border">
              <div className="min-w-0">
                <div className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Full email preview</div>
                <div className="text-sm font-sans text-hg-text truncate">{preview.fitter_name} — <span className="text-hg-text-dim">{preview.subject}</span></div>
              </div>
              <button onClick={() => setPreview(null)} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
            </div>
            {previewLoading || !preview.html ? (
              <div className="flex items-center justify-center h-72"><Loader2 size={18} className="animate-spin text-hg-green" /></div>
            ) : (
              <iframe srcDoc={preview.html} title="Check-in email preview" className="w-full border-0 bg-white" style={{ height: '70vh' }} sandbox="allow-same-origin" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* The send job, as the server records it.
 *
 * Every number here is counted by the Hub from the drafts and the send log each time it is
 * asked — not from anything this page remembers — so closing the page and reopening it
 * mid-run shows the same thing. "Confirmed delivered" means MailerLite reports the
 * campaign finished and sent; anything merely handed over is listed as awaiting.
 *
 * The panel never says "failed" for something it does not know. A job whose outcome was
 * never recorded is "interrupted", and a fitter caught mid-send is "outcome unknown".
 */
function SendJobPanel({ job, onRelease }) {
  const c = job.counts || {};
  const running = job.status === 'running';
  const clean = job.status === 'finished' && !c.failed && !c.unknown && !c.awaiting && !c.not_attempted;
  const tone = running ? 'text-hg-text bg-hg-bg/60 border-hg-border'
    : clean ? 'text-hg-green bg-hg-green/10 border-hg-green/30'
      : job.status === 'finished' ? 'text-amber-300 bg-amber-400/10 border-amber-400/30'
        : 'text-red-300 bg-red-400/10 border-red-400/30';
  const minsLeft = Math.max(1, Math.round(((job.eta_seconds || 0) - (job.elapsed_seconds || 0)) / 60));
  const when = (ts) => { try { return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch { return ts; } };
  let headline;
  if (running && job.phase === 'reconciling') {
    headline = `All ${job.total} attempted — confirming delivery with MailerLite. This takes a few minutes.`;
  } else if (running) {
    headline = `Send in progress — ${job.attempted} of ${job.total} attempted`
      + (job.current ? ` · now: ${job.current}` : '') + ` · about ${minsLeft} min left`;
  } else if (job.status === 'finished') {
    headline = `Send finished at ${when(job.finished_at)}.`;
  } else if (job.status === 'interrupted') {
    headline = `Send INTERRUPTED at ${when(job.finished_at)} — the Hub restarted or the job stopped mid-run. `
      + 'Nobody is re-sent automatically. Press Send to carry on: everyone already sent is skipped.';
  } else if (job.status === 'crashed') {
    headline = `Send STOPPED ON AN ERROR at ${when(job.finished_at)}: ${job.error || 'no reason recorded'}. `
      + 'Press Send to carry on: everyone already sent is skipped.';
  } else {
    headline = `Send is in a state this page does not recognise (${job.status}). Treat the outcome as unknown.`;
  }
  const rep = job.report || {};
  return (
    <div className={`text-xs font-sans rounded-sm px-3 py-2 border space-y-1 ${tone}`} data-testid="fc-send-job" data-status={job.status}>
      <div className="flex items-start gap-2">
        {running ? <Loader2 size={12} className="mt-0.5 shrink-0 animate-spin" />
          : clean ? <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
            : <AlertTriangle size={12} className="mt-0.5 shrink-0" />}
        <span>{headline}</span>
      </div>
      <div className="pl-5" data-testid="fc-send-job-counts">
        {c.delivered ?? '?'} confirmed delivered · {c.awaiting ?? '?'} awaiting confirmation (not yet sent) ·{' '}
        {c.failed ?? '?'} not delivered · {c.unknown ?? '?'} outcome unknown ·{' '}
        {c.not_attempted ?? '?'} not attempted · {c.skipped ?? '?'} skipped · {c.total ?? '?'} in the run
      </div>
      <div className="pl-5 opacity-80" data-testid="fc-send-job-report">
        {running && `You can close this page. A report will be emailed to ${rep.to} when the send ends.`}
        {!running && rep.sent_at && `Report emailed to ${rep.to} at ${when(rep.sent_at)}: "${rep.subject}"`}
        {!running && !rep.sent_at && rep.error && `The report email to ${rep.to} did NOT go (${rep.error}). This page is the report.`}
        {!running && !rep.sent_at && !rep.error && `The report email to ${rep.to} has not gone yet.`}
      </div>
      {(job.unknown || []).map((u) => (
        <div key={u.draft_id} className="pl-5 flex items-start gap-2" data-testid="fc-send-job-unknown">
          <span><strong>{u.name}</strong> {u.email} — {u.reason}</span>
          {!running && u.draft_id && (
            <button onClick={() => onRelease(u)} className="ml-auto shrink-0 px-2 py-1 border border-current rounded-sm hover:opacity-80">
              Release
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/* The unmatched banner, with a memory.
 *
 * It used to be one yellow line naming the first six rows, and nothing you could do
 * about them. So the same eight names came back every month — Randall answered them in
 * conversation, the answer went nowhere, and a banner that is mostly settled business is
 * a banner nobody reads. The one row that genuinely needs him goes with the rest.
 *
 * Map and Ignore here write a RULE (who decided, when, why), not a one-off. Every future
 * run consults it. An ignore is reversible and stays visible under "already decided" —
 * a decision that hides the thing it decided about cannot be checked.
 *
 * Mapping to somebody with no Hub record does NOT create them: the rule is stored
 * pending, the row moves out of the banner, and an onboarding item goes to Randall.
 * Fitter creation is human-confirmed, never a side effect of clicking Map.
 */
// The sales sitting against a rule whose fitter has no Hub record yet, as their check-in WOULD
// count them (fee off, never below $0 — fitter_checkin_logic.pending_rule_sales). Read-only:
// nothing is attributed or created; the day the record exists these become their draft.
function PendingRuleSales({ s }) {
  if (!s) return null;
  const label = s.period === 'ytd' ? 'Year to date' : 'This month';
  return (
    <div className="text-hg-text" data-testid={`fc-pending-sales-${s.qb_name}`}>
      {label}: {money(s.real)} real sales
      <span className="text-hg-text-dim"> ({money(s.total)} in QuickBooks)</span>
      {s.py != null && <> · PY {money(s.py)}{s.pct_vs_py != null && ` (${s.pct_vs_py > 0 ? '+' : ''}${s.pct_vs_py}%)`}</>}
      {s.py == null && s.pp != null && <> · PM {money(s.pp)}</>}
      <span className="text-hg-text-dim"> · not counted for anyone until their record exists</span>
    </div>
  );
}

function UnmatchedBanner({ run, runId, onChanged, sales = [] }) {
  const [rules, setRules] = useState([]);
  const [fitters, setFitters] = useState([]);
  const [open, setOpen] = useState(null);      // qb_name being decided
  const [action, setAction] = useState('ignore');
  const [fitterId, setFitterId] = useState('');
  const [fitterName, setFitterName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showDecided, setShowDecided] = useState(false);

  const unmatched = run.unmatched || [];
  const pending = run.pending_rules || [];
  const decided = (run.ignored || []).filter((r) => r.rule);

  const loadRules = useCallback(async () => {
    try {
      const [r, f] = await Promise.all([
        axios.get(`${API}/fitter-checkin/qb-name-rules`),
        axios.get(`${API}/fitters`),
      ]);
      setRules(r.data.rules || []);
      setFitters((f.data.fitters || []).filter((x) => !x.is_test));
    } catch { /* the banner still works without the rule list */ }
  }, []);
  useEffect(() => { loadRules(); }, [loadRules]);

  const startDecision = (name) => {
    setOpen(name); setAction('ignore'); setFitterId(''); setFitterName(''); setReason(''); setMsg('');
  };

  const reevaluate = async () => {
    const r = await axios.post(`${API}/fitter-checkin/runs/${runId}/reevaluate-unmatched`);
    await loadRules();
    if (onChanged) await onChanged();
    return r.data;
  };

  const saveRule = async () => {
    if (!reason.trim()) { setMsg('A rule needs a reason — it is what makes it checkable later.'); return; }
    if (action === 'map_to_fitter' && !fitterId && !fitterName.trim()) {
      setMsg('Pick a fitter, or type the name of the one who has no Hub record yet.'); return;
    }
    setBusy(true); setMsg('');
    try {
      await axios.post(`${API}/fitter-checkin/qb-name-rules`, {
        qb_name: open, action, reason,
        fitter_id: fitterId || null, fitter_name: fitterId ? null : (fitterName.trim() || null),
      });
      const out = await reevaluate();
      const stillPending = (out.pending_rules || []).some((r) => r.name === open);
      setOpen(null);
      setMsg(stillPending
        ? 'Rule stored. That fitter has no Hub record yet — onboard them and it attaches automatically.'
        : 'Rule stored and applied to this run.');
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not store the rule.'); }
    setBusy(false);
  };

  const undoRule = async (ruleId) => {
    if (!window.confirm('Withdraw this rule? The name returns to the unmatched list.')) return;
    setBusy(true);
    try { await axios.delete(`${API}/fitter-checkin/qb-name-rules/${ruleId}`); await reevaluate(); setMsg('Rule withdrawn.'); }
    catch (e) { setMsg(e.response?.data?.detail || 'Could not withdraw the rule.'); }
    setBusy(false);
  };

  if (!unmatched.length && !pending.length && !decided.length) return null;

  return (
    <div className="space-y-2" data-testid="fc-unmatched">
      {unmatched.length > 0 && (
        <div className="text-[11px] font-sans text-yellow-400 bg-yellow-400/5 border border-yellow-400/20 rounded-sm px-3 py-2 space-y-1">
          <div>{unmatched.length} unmatched QuickBooks row{unmatched.length === 1 ? '' : 's'} — decide once and the Hub remembers.</div>
          {unmatched.map((u) => (
            <div key={u.name} className="flex items-center gap-2 flex-wrap">
              <span className="text-hg-text">{u.name}</span>
              <span className="text-hg-text-dim">{money(u.total)}</span>
              <button data-testid={`fc-decide-${u.name}`} onClick={() => startDecision(u.name)}
                className="ml-auto px-2 py-0.5 border border-yellow-400/40 rounded-sm hover:bg-yellow-400/10">
                Map or ignore
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="border border-hg-border bg-hg-surface rounded-sm px-3 py-2 space-y-2 text-[11px] font-sans" data-testid="fc-rule-form">
          <div className="text-hg-text">What is <strong>{open}</strong>?</div>
          <div className="flex gap-2">
            <button onClick={() => setAction('ignore')} className={`px-2 py-1 rounded-sm border ${action === 'ignore' ? 'border-hg-green text-hg-green' : 'border-hg-border text-hg-text-dim'}`}>Not a fitter — ignore</button>
            <button onClick={() => setAction('map_to_fitter')} className={`px-2 py-1 rounded-sm border ${action === 'map_to_fitter' ? 'border-hg-green text-hg-green' : 'border-hg-border text-hg-text-dim'}`}>It is a fitter — map</button>
          </div>
          {action === 'map_to_fitter' && (
            <div className="flex gap-2 flex-wrap">
              <select data-testid="fc-rule-fitter" value={fitterId} onChange={(e) => setFitterId(e.target.value)}
                className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-hg-text">
                <option value="">— fitter with a Hub record —</option>
                {fitters.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              {!fitterId && (
                <input data-testid="fc-rule-fitter-name" value={fitterName} onChange={(e) => setFitterName(e.target.value)}
                  placeholder="…or a new fitter's name (they will need onboarding)"
                  className="flex-1 min-w-[16rem] bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-hg-text" />
              )}
            </div>
          )}
          <input data-testid="fc-rule-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why — this is stored with your name and the date"
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-hg-text" />
          <div className="flex gap-2">
            <button data-testid="fc-rule-save" onClick={saveRule} disabled={busy}
              className="px-3 py-1 bg-hg-green text-hg-text rounded-sm disabled:opacity-40">
              {busy ? <Loader2 size={11} className="animate-spin" /> : 'Save rule'}
            </button>
            <button onClick={() => setOpen(null)} className="px-3 py-1 border border-hg-border text-hg-text-dim rounded-sm">Cancel</button>
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="text-[11px] font-sans text-amber-300 bg-amber-400/5 border border-amber-400/20 rounded-sm px-3 py-2 space-y-1" data-testid="fc-pending-rules">
          {pending.map((u) => (
            <div key={u.name}>
              <span className="text-hg-text">{u.name}</span> — decided: {u.rule?.fitter_name || 'a fitter'}, who has no Hub record yet.
              <PendingRuleSales s={(sales || []).find((x) => x.qb_name === u.name)} />
              <div className="text-hg-text-dim/80">{u.rule?.state_note} · Onboard them and this attaches automatically.</div>
            </div>
          ))}
        </div>
      )}

      {decided.length > 0 && (
        <div className="text-[11px] font-sans text-hg-text-dim">
          <button onClick={() => setShowDecided(!showDecided)} className="hover:text-hg-text" data-testid="fc-decided-toggle">
            {decided.length} name{decided.length === 1 ? '' : 's'} already decided {showDecided ? '▾' : '▸'}
          </button>
          {showDecided && (
            <div className="mt-1 space-y-1 pl-2 border-l border-hg-border">
              {decided.map((u) => (
                <div key={u.name}>
                  <span className="text-hg-text">{u.name}</span> — {u.rule.action === 'ignore' ? 'ignored' : `mapped to ${u.rule.fitter_name}`}
                  {u.rule.attributed_fitter_name ? ` (this account is ${u.rule.attributed_fitter_name})` : ''}
                  <div className="text-hg-text-dim/80">
                    {u.rule.reason} — {u.rule.decided_by}, {(u.rule.decided_at || '').slice(0, 10)}
                    <button onClick={() => undoRule(u.rule.rule_id)} disabled={busy} className="ml-2 underline hover:text-hg-text">withdraw</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {msg && <div className="text-[11px] font-sans text-hg-green" data-testid="fc-rule-msg">{msg}</div>}
    </div>
  );
}

function DigestSection({ runId, dryRun }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/fitter-checkin/runs/${runId}/digest`); setData(r.data); }
    catch (e) { setMsg(e.response?.data?.detail || 'Failed to load digest.'); }
    setLoading(false);
  }, [runId]);
  const toggle = () => { const n = !open; setOpen(n); if (n && !data) load(); };

  const act = async (key, url) => {
    setBusy(key); setMsg('');
    try { const r = await axios.post(`${API}${url}`); setMsg(JSON.stringify(r.data?.recipients || r.data?.managers || r.data)); await load(); }
    catch (e) { setMsg(e.response?.data?.detail || 'Send failed.'); }
    setBusy('');
  };

  const dg = data?.digest;
  const fl = dg?.flags || {};
  const rollups = data?.manager_rollups || [];

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm" data-testid="fc-digest">
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-2.5 border-b border-hg-border">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-hg-green" />
          <span className="text-xs font-sans text-hg-text uppercase tracking-wider">Internal digest &amp; roll-ups</span>
          <span className="text-[10px] text-hg-text-dim">(internal eyes only)</span>
        </div>
        <span className="text-[10px] text-hg-text-dim">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="p-4 space-y-4">
          {loading && <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-hg-green" /></div>}
          {dg && (
            <>
              <div className="flex flex-wrap gap-2">
                {Object.entries(dg.bucket_counts || {}).map(([b, n]) => (
                  <span key={b} className="text-[10px] font-sans px-2 py-1 rounded-sm border border-hg-border" style={{ color: BUCKET_META[b]?.color }}>
                    {BUCKET_META[b]?.label || b}: <b>{n}</b>
                  </span>
                ))}
                <span className="text-[10px] font-sans px-2 py-1 rounded-sm bg-hg-green/10 text-hg-green">Sent {dg.sent_count}/{dg.total}</span>
              </div>

              {[['Big drops', fl.big_drops], ['Multi-month quiet', fl.multi_month_quiet], ['Needs follow-up', fl.needs_followup]].map(([t, list]) => (
                (list && list.length > 0) ? (
                  <div key={t} className="text-[11px] font-sans text-hg-text-dim"><b className="text-hg-text">{t}:</b> {list.map((r) => r.name).join(', ')}</div>
                ) : null
              ))}
              {(dg.needs_human?.unmatched?.length > 0 || dg.needs_human?.no_email?.length > 0) && (
                <div className="text-[11px] font-sans text-red-400 bg-red-400/5 border border-red-400/20 rounded-sm px-3 py-2">
                  Needs a human — {dg.needs_human.no_email?.length > 0 && `no email: ${dg.needs_human.no_email.map((r) => r.name).join(', ')}`}
                  {dg.needs_human.no_email?.length > 0 && dg.needs_human.unmatched?.length > 0 && ' · '}
                  {dg.needs_human.unmatched?.length > 0 && `unmatched: ${dg.needs_human.unmatched.map((u) => u.name).join(', ')}`}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-[11px] font-sans" data-testid="fc-digest-table">
                  <thead><tr className="text-hg-text-dim text-left border-b border-hg-border">
                    <th className="py-1.5 pr-2">Fitter</th><th className="py-1.5 pr-2">Status</th>
                    <th className="py-1.5 pr-2 text-right">Month</th><th className="py-1.5 pr-2 text-right">YTD</th>
                    <th className="py-1.5 pr-2">vs baseline</th><th className="py-1.5">Sent</th>
                  </tr></thead>
                  <tbody>
                    {(dg.rows || []).map((r) => (
                      <tr key={r.fitter_id} className="border-b border-hg-border/30">
                        <td className="py-1.5 pr-2 text-hg-text">{r.name}{!r.email && <span className="text-red-400"> ⚠</span>}</td>
                        <td className="py-1.5 pr-2" style={{ color: BUCKET_META[r.bucket]?.color }}>{BUCKET_META[r.bucket]?.label || r.bucket}</td>
                        <td className="py-1.5 pr-2 text-right text-hg-text">{money(r.monthly_real)}</td>
                        <td className="py-1.5 pr-2 text-right text-hg-text-dim">{money(r.ytd_real)}</td>
                        <td className="py-1.5 pr-2 text-hg-text-dim">{r.comp_val != null ? `${r.comp_label}: ${money(r.comp_val)}` : '—'}{r.pct_change != null ? ` (${r.pct_change > 0 ? '+' : ''}${r.pct_change}%)` : ''}</td>
                        <td className="py-1.5 text-hg-green">{r.sent ? '✓' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {rollups.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Account-manager roll-ups</div>
                  {rollups.map((ro) => (
                    <div key={ro.manager_id} className="text-[11px] font-sans text-hg-text-dim border border-hg-border/50 rounded-sm px-3 py-2">
                      <b className="text-hg-text">{ro.manager_name}</b> ({ro.manager_email}) — {ro.rows.length} fitters · follow up: {ro.followup.map((r) => r.name).join(', ') || 'none'}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap border-t border-hg-border pt-3">
                <TestBtn testid="fc-digest-test" icon={Mail} busy={busy === 'dt'} onClick={() => act('dt', `/fitter-checkin/runs/${runId}/send-digest?test_to=${encodeURIComponent(TEST_RECIPIENTS.join(','))}`)}>Send digest to test (me + Grant)</TestBtn>
                <TestBtn testid="fc-rollup-test" icon={Users} busy={busy === 'rt'} onClick={() => act('rt', `/fitter-checkin/runs/${runId}/send-manager-rollups?test_to=${encodeURIComponent(TEST_RECIPIENTS.join(','))}`)}>Send roll-ups to test (me + Grant)</TestBtn>
                <button data-testid="fc-digest-send" onClick={() => { if (window.confirm(`Send the internal digest to all team recipients: ${(data.digest_recipients || []).join(', ')}?`)) act('ds', `/fitter-checkin/runs/${runId}/send-digest`); }}
                  disabled={busy || dryRun}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
                  {busy === 'ds' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send digest to team
                </button>
                <button data-testid="fc-rollup-send" onClick={() => { if (window.confirm('Send each account manager their own roster roll-up?')) act('rs', `/fitter-checkin/runs/${runId}/send-manager-rollups`); }}
                  disabled={busy || dryRun}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40">
                  {busy === 'rs' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send manager roll-ups
                </button>
                {dryRun && <span className="text-[10px] text-yellow-400 font-sans">dry-run — real digest/roll-up sends disabled (test buttons still work)</span>}
              </div>
            </>
          )}
          {msg && <div className="text-[11px] font-sans text-hg-text bg-hg-bg/60 border border-hg-border rounded-sm px-3 py-2 break-all" data-testid="fc-digest-msg">{msg}</div>}
        </div>
      )}
    </div>
  );
}

function BucketBlock({ bucket, drafts, onPatch, onBulkApprove, onPreview }) {
  const meta = BUCKET_META[bucket] || { label: bucket, color: '#888' };
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-hg-border">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
          <span className="text-xs font-sans text-hg-text uppercase tracking-wider">{meta.label}</span>
          <span className="text-[10px] text-hg-text-dim">({drafts.length})</span>
        </div>
        <button data-testid={`fc-approve-bucket-${bucket}`} onClick={onBulkApprove} className="text-[10px] font-sans text-hg-green hover:underline">Approve all pending</button>
      </div>
      <div className="divide-y divide-hg-border/40">
        {drafts.map((d) => <DraftRow key={d.id} d={d} onPatch={onPatch} onPreview={onPreview} />)}
      </div>
    </div>
  );
}

function DraftRow({ d, onPatch, onPreview }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(d.subject);
  const [body, setBody] = useState(d.body);
  // pending_confirm is already with MailerLite, so the backend rejects edits on it too.
  // Offering buttons that 400 is worse than not offering them.
  const sent = d.status === 'sent' || d.status === 'pending_confirm';
  return (
    <div className="px-4 py-3" data-testid={`fc-draft-${d.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-sans text-hg-text">{d.fitter_name} <span className="text-[10px] text-hg-text-dim">{d.email || '(no email)'}</span></div>
          <div className="text-[11px] text-hg-text-dim font-sans">
            Month {money(d.monthly_real)}
            {d.baseline_label === 'prior_year' && d.monthly_py != null ? ` (PY ${money(d.monthly_py)})`
              : d.baseline_label === 'prior_month' && d.monthly_pp != null ? ` (PM ${money(d.monthly_pp)})` : ''}
            {' · '}YTD {money(d.ytd_real)}{d.ytd_py != null && ` (PY ${money(d.ytd_py)})`}
            {d.pct_change != null && ` · ${d.pct_change > 0 ? '+' : ''}${d.pct_change}%`} · baseline: {d.baseline_label}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${
            d.status === 'approved' ? (d.last_error ? 'bg-red-400/10 text-red-300' : 'bg-hg-green/10 text-hg-green')
              : d.status === 'sent' ? 'bg-hg-green/20 text-hg-green'
              : d.status === 'pending_confirm' ? 'bg-amber-400/10 text-amber-300'
              : d.status === 'excluded' ? 'bg-hg-border text-hg-text-dim line-through' : 'bg-yellow-500/10 text-yellow-400'}`}>
            {d.status === 'pending_confirm' ? 'unconfirmed' : d.status === 'approved' && d.last_error ? 'not reached' : d.status}</span>
          {!editing && (
            <button data-testid={`fc-preview-${d.id}`} onClick={() => onPreview && onPreview(d.id)} title="Preview the full email as the fitter will receive it" className="text-hg-text-dim hover:text-hg-green"><Eye size={13} /></button>
          )}
          {!sent && !editing && (
            <>
              <button data-testid={`fc-edit-${d.id}`} onClick={() => setEditing(true)} className="text-hg-text-dim hover:text-hg-green"><Edit2 size={13} /></button>
              {d.status !== 'approved' && <button data-testid={`fc-approve-${d.id}`} onClick={() => onPatch(d.id, { status: 'approved' })} className="text-hg-text-dim hover:text-hg-green"><CheckCircle2 size={14} /></button>}
              {d.status !== 'excluded' && <button data-testid={`fc-exclude-${d.id}`} onClick={() => onPatch(d.id, { status: 'excluded' })} className="text-hg-text-dim hover:text-red-400"><X size={14} /></button>}
            </>
          )}
        </div>
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-xs text-hg-text font-sans" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="w-full bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-xs text-hg-text font-sans" />
          <div className="flex gap-2">
            <button onClick={async () => { await onPatch(d.id, { subject, body }); setEditing(false); }} className="inline-flex items-center gap-1 px-3 py-1 text-[11px] bg-hg-green text-hg-text rounded-sm"><Save size={11} /> Save</button>
            <button onClick={() => { setSubject(d.subject); setBody(d.body); setEditing(false); }} className="px-3 py-1 text-[11px] border border-hg-border text-hg-text-dim rounded-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <details className="mt-1">
          <summary className="text-[11px] text-hg-text-dim font-sans cursor-pointer">{d.subject}</summary>
          <pre className="mt-1 whitespace-pre-wrap text-[11px] text-hg-text font-sans bg-hg-bg/40 rounded-sm p-2">{d.body}</pre>
        </details>
      )}
    </div>
  );
}
